import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FileText, Plus, ArrowRight, Edit, LogOut } from 'lucide-react';
import { Logo } from '../components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Cases() {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [user, setUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    case_number: '',
    title: '',
    description: '',
    client_id: '',
    client_name: '',
    status: 'active',
    case_type: '',
    court_name: '',
  });

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      setUser(JSON.parse(userStr));
    }
    fetchCases();
  }, []);

  const fetchCases = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/cases`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCases(response.data);
    } catch (err) {
      console.error('Error fetching cases:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/cases`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShowModal(false);
      fetchCases();
      setFormData({
        case_number: '',
        title: '',
        description: '',
        client_id: '',
        client_name: '',
        status: 'active',
        case_type: '',
        court_name: '',
      });
    } catch (err) {
      console.error('Error creating case:', err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-sand" dir="rtl" lang="ar">
      <header className="bg-primary py-4 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ArrowRight className="w-6 h-6 text-white" />
            </button>
            <Logo size="md" />
            <h1 className="text-2xl font-heading font-bold text-white">القضايا</h1>
          </div>
          <div className="flex gap-3">
            {user?.role !== 'client' && (
              <button
                data-testid="add-case-btn"
                onClick={() => setShowModal(true)}
                className="flex items-center gap-2 px-6 py-2 bg-gold text-white rounded-md hover:bg-gold-dark transition-all duration-300"
              >
                <Plus className="w-5 h-5" />
                إضافة قضية
              </button>
            )}
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-all"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              خروج
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cases.map((c) => (
            <div
              key={c.id}
              className="bg-white rounded-lg p-6 shadow-sm border border-gray-100 hover-lift"
              data-testid={`case-card-${c.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <FileText className="w-8 h-8 text-gold" />
                <span
                  className={`px-3 py-1 rounded-full text-xs font-body ${
                    c.status === 'active'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {c.status === 'active' ? 'نشطة' : c.status}
                </span>
              </div>
              <h3 className="text-lg font-heading font-semibold text-primary mb-2">
                {c.title}
              </h3>
              <p className="text-sm text-gray-600 mb-2 font-body">
                رقم القضية: {c.case_number}
              </p>
              <p className="text-sm text-gray-600 mb-2 font-body">العميل: {c.client_name}</p>
              <p className="text-sm text-gray-600 mb-2 font-body">نوع القضية: {c.case_type}</p>
              {c.court_name && (
                <p className="text-sm text-gray-600 font-body">المحكمة: {c.court_name}</p>
              )}
            </div>
          ))}
        </div>

        {cases.length === 0 && (
          <div className="text-center py-16" data-testid="no-cases">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 font-body">لا توجد قضايا حاليا</p>
          </div>
        )}
      </main>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 max-w-2xl w-full mx-4" data-testid="case-modal">
            <h2 className="text-2xl font-heading font-bold text-primary mb-6">
              إضافة قضية جديدة
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  data-testid="case-number-input"
                  type="text"
                  placeholder="رقم القضية"
                  value={formData.case_number}
                  onChange={(e) => setFormData({ ...formData, case_number: e.target.value })}
                  required
                  className="px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
                />
                <input
                  data-testid="case-title-input"
                  type="text"
                  placeholder="عنوان القضية"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
                />
              </div>
              <textarea
                data-testid="case-description-input"
                placeholder="وصف القضية"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                rows={3}
                className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  data-testid="case-client-id-input"
                  type="text"
                  placeholder="معرف العميل"
                  value={formData.client_id}
                  onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                  required
                  className="px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
                />
                <input
                  data-testid="case-client-name-input"
                  type="text"
                  placeholder="اسم العميل"
                  value={formData.client_name}
                  onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  required
                  className="px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  data-testid="case-type-input"
                  type="text"
                  placeholder="نوع القضية"
                  value={formData.case_type}
                  onChange={(e) => setFormData({ ...formData, case_type: e.target.value })}
                  required
                  className="px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
                />
                <input
                  data-testid="case-court-input"
                  type="text"
                  placeholder="اسم المحكمة"
                  value={formData.court_name}
                  onChange={(e) => setFormData({ ...formData, court_name: e.target.value })}
                  className="px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
                />
              </div>
              <div className="flex gap-4 justify-end">
                <button
                  data-testid="case-cancel-btn"
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-all font-body"
                >
                  إلغاء
                </button>
                <button
                  data-testid="case-submit-btn"
                  type="submit"
                  className="px-6 py-2 bg-gold text-white rounded-md hover:bg-gold-dark transition-all font-body"
                >
                  حفظ
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
